import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MessageSquare, Printer } from 'lucide-react';
import './Footer.css';

const Footer = () => {
  // Real links (round 5) so crawlers can follow them; same look and behaviour
  // as the old buttons: go to the page, then scroll smoothly to the top.
  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-section">
            <img 
              src="/images/logos/clinical-minds-logo-white.png" 
              alt="Psychology Practice Logo" 
              className="footer-logo"
            />
            <p className="body-small">
              Neurodevelopmental assessment and psychology. Coffs Harbour, visiting clinics, and Australia-wide via Telehealth.
            </p>
          </div>
          
          <div className="footer-section">
            <h4 className="footer-title">Quick Links</h4>
            <div className="footer-links">
              <Link to="/about" onClick={scrollTop} className="footer-link">About Us</Link>
              <Link to="/services" onClick={scrollTop} className="footer-link">Services</Link>
              <Link to="/refer" onClick={scrollTop} className="footer-link">Refer</Link>
              <Link to="/faqs" onClick={scrollTop} className="footer-link">FAQs & Forms</Link>
              <Link to="/bookings" onClick={scrollTop} className="footer-link">Book Appointment</Link>
            </div>
          </div>
          
          <div className="footer-section">
            <h4 className="footer-title">Locations</h4>
            <div className="footer-links">
              <Link to="/locations/coffs-harbour" onClick={scrollTop} className="footer-link">Coffs Harbour</Link>
              <Link to="/clinics" onClick={scrollTop} className="footer-link">Visiting Clinics</Link>
              <Link to="/visiting-clinics/port-macquarie" onClick={scrollTop} className="footer-link">Port Macquarie</Link>
              <Link to="/visiting-clinics/sydney" onClick={scrollTop} className="footer-link">Sydney</Link>
              <Link to="/visiting-clinics/melbourne" onClick={scrollTop} className="footer-link">Melbourne</Link>
              <Link to="/visiting-clinics/gold-coast" onClick={scrollTop} className="footer-link">Gold Coast</Link>
              <Link to="/visiting-clinics/hamilton" onClick={scrollTop} className="footer-link">Hamilton VIC</Link>
              <Link to="/locations/telehealth" onClick={scrollTop} className="footer-link">Telehealth</Link>
            </div>
          </div>
          
          <div className="footer-section">
            <h4 className="footer-title">Contact</h4>
            <div className="footer-contact">
              <div className="contact-item">
                <Phone size={16} />
                <span><a href="tel:0266002710">02 6600 2710 (phone)</a></span>
              </div>
              <div className="contact-item">
                <MessageSquare size={16} />
                <span><a href="sms:0415220944">0415 220 944 (SMS)</a></span>
              </div>
              <div className="contact-item">
                <Printer size={16} />
                <span><a href="tel:0266991457">02 6699 1457 (Fax)</a></span>
              </div>
              <div className="contact-item">
                <Mail size={16} />
                <span><a href="mailto:contact@clinicalminds.net">contact@clinicalminds.net</a></span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <div className="footer-bottom-left">
            <p className="body-small">
              © {new Date().getFullYear()} Clinical Minds Psychology. All rights reserved.
            </p>
            <div className="footer-social-links">
              <a 
                href="https://www.linkedin.com/company/clinicalmindspsychology" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-social-link"
                aria-label="LinkedIn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a 
                href="https://www.facebook.com/clinicalmindspsychology" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-social-link"
                aria-label="Facebook"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/clinicalmindspsychology/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-social-link"
                aria-label="Instagram"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>
          <div className="footer-legal-links">
            <Link to="/privacy-policy" onClick={scrollTop} className="footer-legal-link">Privacy Policy</Link>
            <span className="footer-separator">•</span>
            <Link to="/terms-and-conditions" onClick={scrollTop} className="footer-legal-link">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
